import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
  Skeleton,
  useTheme,
} from '@mui/material'
import {
  Search as SearchIcon,
  ArticleOutlined,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  RocketLaunch as GettingStartedIcon,
  People as ClientsIcon,
  CalendarMonth as ScheduleIcon,
  Payments as PaymentsIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { refetch, resolved, useQuery } from '@hapana/shared/web/services/gqty'
import { useSupportAssistant } from '../support-assistant.hooks'
import { useDebounce } from '@hapana/shared/web/hooks/useDebounce'
import DOMPurify from 'dompurify'
import { markdownToHtml } from '@hapana/shared/web/utils/markdown-to-html.util'
import { ArticleViewer } from '@hapana/shared/web/components/shared/article-viewer'
import { z } from 'zod'
import { styles } from './docs-tab.styles'

const searchResultItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  snippet: z.string(),
  category: z.string(),
  score: z.number(),
  content: z.string().optional(),
})

type SearchResultItem = z.infer<typeof searchResultItemSchema>

const selectedArticleSchema = z.object({
  slug: z.string(),
  title: z.string(),
})

type SelectedArticle = z.infer<typeof selectedArticleSchema>

const FEATURED_CATEGORIES = [
  { slug: 'getting-started', icon: GettingStartedIcon, i18nKey: 'gettingStarted' },
  { slug: 'payments', icon: PaymentsIcon, i18nKey: 'payments' },
  { slug: 'client-management', icon: ClientsIcon, i18nKey: 'clientManagement' },
  { slug: 'scheduling', icon: ScheduleIcon, i18nKey: 'scheduling' },
  { slug: 'reports', icon: ReportsIcon, i18nKey: 'reports' },
  { slug: 'settings', icon: SettingsIcon, i18nKey: 'settings' },
]

interface CategoryArticle {
  slug: string
  title: string
  category: string
}

interface DocsTabProps {
  initialArticleSlug?: string | null
  /** When true, removes internal horizontal padding (for full-page layout where parent handles width) */
  fullWidth?: boolean
}

export function DocsTab({ initialArticleSlug, fullWidth }: DocsTabProps = {}) {
  const { t } = useTranslation('core')
  const theme = useTheme()
  const {
    setActiveTab,
    sendMessage,
    planTier,
    userRole,
    scopeLevel,
    pendingDocsArticle,
    clearPendingDocsArticle,
  } = useSupportAssistant()
  const query = useQuery({ suspense: false }) as any

  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<SearchResultItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Article detail state
  const [selectedArticle, setSelectedArticle] = useState<SelectedArticle | null>(null)
  const [articleContent, setArticleContent] = useState('')
  const [articleCategory, setArticleCategory] = useState('')
  const [articleTags, setArticleTags] = useState<string[]>([])
  const [isLoadingArticle, setIsLoadingArticle] = useState(false)

  // Category browsing state
  const [browsedCategory, setBrowsedCategory] = useState<{ slug: string; name: string } | null>(
    null
  )
  const [categoryArticles, setCategoryArticles] = useState<CategoryArticle[]>([])
  const [isLoadingCategory, setIsLoadingCategory] = useState(false)

  const debouncedQuery = useDebounce(searchQuery, 500)

  const performSearch = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm.trim()) return

      setIsSearching(true)
      setHasSearched(true)

      try {
        const data = await refetch(() => {
          const response = query.searchKnowledge({
            input: {
              query: searchTerm,
              corporateId: 'default',
              limit: 5,
              planTier,
              userRole,
              scopeLevel,
            },
          })

          return {
            results: response.results.map((r: any) => ({
              id: r.document.id,
              title: r.document.title,
              snippet: r.relevantSnippet,
              category: r.document.category,
              score: r.score,
              content: r.document.content,
            })),
            total: response.total,
          }
        })

        setResults((data.results || []).filter((r: SearchResultItem) => r.id && r.title))
      } catch (error) {
        console.error('[DocsTab] Search error:', error)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query is a gqty proxy that changes every render
    [planTier, userRole, scopeLevel]
  )

  // Use a ref so the effect doesn't re-run when performSearch changes
  const performSearchRef = useRef(performSearch)
  performSearchRef.current = performSearch

  // Auto-search on debounced query change
  useEffect(() => {
    if (debouncedQuery.trim()) {
      performSearchRef.current(debouncedQuery)
      // Clear category browsing when searching
      setBrowsedCategory(null)
      setCategoryArticles([])
    } else if (!debouncedQuery.trim()) {
      setResults([])
      setHasSearched(false)
    }
  }, [debouncedQuery])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      performSearch(searchQuery)
    }
  }

  const handleAskAI = () => {
    if (searchQuery.trim()) {
      sendMessage(searchQuery)
      setActiveTab('chats')
    }
  }

  const handleSelectArticle = useCallback(
    async (result: SearchResultItem) => {
      setSelectedArticle({ slug: result.id, title: result.title })
      setIsLoadingArticle(true)
      setArticleCategory(result.category || '')

      try {
        const data = await refetch(() => {
          const article = query.knowledgeArticle({ slug: result.id })
          return {
            contentMarkdown: article.contentMarkdown,
            category: article.category,
            tags: article.tags,
            title: article.title,
          }
        })

        setArticleContent(markdownToHtml(data.contentMarkdown || ''))
        if (data.category) setArticleCategory(data.category)
        setArticleTags(data.tags || [])
      } catch {
        // Fallback: use content from Vertex AI search result
        if (result.content) {
          setArticleContent(markdownToHtml(result.content))
        } else {
          setArticleContent(DOMPurify.sanitize(`<p>${result.snippet || ''}</p>`))
        }
      } finally {
        setIsLoadingArticle(false)
      }
    },
    [query]
  )

  const loadArticleBySlug = useCallback(
    async (slug: string, title: string) => {
      setSelectedArticle({ slug, title })
      setIsLoadingArticle(true)
      setArticleCategory('')

      try {
        const data = await refetch(() => {
          const article = query.knowledgeArticle({ slug })
          return {
            contentMarkdown: article?.contentMarkdown,
            category: article?.category,
            tags: article?.tags,
            title: article?.title,
          }
        })

        if (!data?.contentMarkdown) {
          setArticleContent(`<p>${t('supportAssistant.errors.unableToLoadArticle')}</p>`)
          return
        }

        setArticleContent(markdownToHtml(data.contentMarkdown))
        if (data.category) setArticleCategory(data.category)
        if (data.title) setSelectedArticle((prev) => (prev ? { ...prev, title: data.title } : prev))
        setArticleTags(data.tags || [])
      } catch {
        setArticleContent(`<p>${t('supportAssistant.errors.unableToLoadArticle')}</p>`)
      } finally {
        setIsLoadingArticle(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query is a gqty proxy that changes every render
    []
  )

  const loadArticleBySlugRef = useRef(loadArticleBySlug)
  loadArticleBySlugRef.current = loadArticleBySlug

  useEffect(() => {
    if (pendingDocsArticle) {
      loadArticleBySlugRef.current(pendingDocsArticle.slug, pendingDocsArticle.title)
      clearPendingDocsArticle()
    }
  }, [pendingDocsArticle, clearPendingDocsArticle])

  // Load article from URL query param (full-page context)
  const lastProcessedSlug = useRef<string | null>(null)
  useEffect(() => {
    if (initialArticleSlug && initialArticleSlug !== lastProcessedSlug.current) {
      lastProcessedSlug.current = initialArticleSlug
      loadArticleBySlugRef.current(initialArticleSlug, initialArticleSlug.replace(/-/g, ' '))
    }
  }, [initialArticleSlug])

  const handleBack = () => {
    setSelectedArticle(null)
    setArticleContent('')
    setArticleCategory('')
    setArticleTags([])
    clearPendingDocsArticle()
  }

  const handleCategoryClick = useCallback(
    async (categorySlug: string, categoryName: string) => {
      setBrowsedCategory({ slug: categorySlug, name: categoryName })
      setIsLoadingCategory(true)
      setCategoryArticles([])

      try {
        const data = await resolved(() => {
          const response = query.knowledgeArticlesByCategory({
            categorySlug,
            limit: 50,
            status: 'PUBLISHED',
          })
          return response.articles.map((a: any) => ({
            slug: a.slug,
            title: a.title,
            category: a.category,
          }))
        })
        setCategoryArticles(data || [])
      } catch (error) {
        console.error('[DocsTab] Error loading category articles:', error)
      } finally {
        setIsLoadingCategory(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- query is a gqty proxy
    []
  )

  const handleCategoryArticleClick = useCallback((slug: string, title: string) => {
    loadArticleBySlugRef.current(slug, title)
  }, [])

  const handleBackToCategories = useCallback(() => {
    setBrowsedCategory(null)
    setCategoryArticles([])
  }, [])

  // Determine if we're in "browse mode" (no search active, fullWidth)
  const showBrowse =
    fullWidth && !hasSearched && !isSearching && !searchQuery.trim() && !selectedArticle

  // Search view (always rendered — article detail is shown below the search input)
  return (
    <Box sx={styles.root}>
      {/* Search Input — always visible */}
      <Box sx={styles.searchInputWrapper(!!fullWidth)}>
        <TextField
          fullWidth
          autoFocus={fullWidth}
          size="small"
          placeholder={t('supportAssistant.docs.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={styles.searchIcon} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isSearching ? (
                  <CircularProgress size={16} />
                ) : searchQuery.trim() ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('')
                      setResults([])
                      setHasSearched(false)
                    }}
                    edge="end"
                    sx={styles.clearButton}
                  >
                    <CloseIcon sx={styles.clearIcon} />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
          sx={styles.searchTextField}
          data-testid="docs-search-input"
        />

        {searchQuery.trim() && (
          <Box sx={styles.askAiChipContainer}>
            <Chip
              label={t('supportAssistant.docs.askAI')}
              onClick={handleAskAI}
              size="small"
              color="primary"
              variant="outlined"
              sx={styles.askAiChip}
              data-testid="docs-ask-ai"
            />
          </Box>
        )}
      </Box>

      {/* Results / Article Detail */}
      <Box sx={styles.resultsContainer(!!fullWidth)}>
        {/* Article detail view */}
        {selectedArticle && (
          <Box sx={styles.articleDetailWrapper}>
            {/* Back header */}
            <Box sx={styles.backHeader}>
              <Box onClick={handleBack} data-testid="docs-back-button" sx={styles.backButton}>
                <ArrowBackIcon sx={styles.backIcon} />
                <Typography variant="body2" sx={styles.backText} noWrap>
                  {t('supportAssistant.docs.backToSearch')}
                </Typography>
              </Box>
            </Box>

            {/* Article content */}
            <ArticleViewer
              title={selectedArticle.title}
              content={articleContent}
              category={articleCategory}
              tags={articleTags}
              isLoading={isLoadingArticle}
              compact
            />
          </Box>
        )}

        {/* Skeleton loader while searching */}
        {!selectedArticle && isSearching && (
          <Box sx={styles.skeletonContainer}>
            <Skeleton variant="text" width={120} height={20} />
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={styles.skeletonCard(theme)}>
                <Box sx={styles.skeletonCardHeader}>
                  <Skeleton variant="text" width="65%" height={22} />
                  <Skeleton variant="rounded" width={60} height={20} />
                </Box>
                <Skeleton variant="text" width="90%" height={16} />
                <Skeleton variant="text" width="70%" height={16} />
              </Box>
            ))}
          </Box>
        )}

        {/* Search Results */}
        {!selectedArticle && !isSearching && hasSearched && results.length > 0 && (
          <Box sx={styles.resultsListContainer}>
            <Typography variant="caption" sx={styles.resultsCountText}>
              {results.length >= 5
                ? t('supportAssistant.docs.topArticlesFound', { count: results.length })
                : t('supportAssistant.docs.articlesFound', { count: results.length })}
            </Typography>

            {results.map((result, index) => (
              <Box
                key={result.id || index}
                onClick={() => handleSelectArticle(result)}
                sx={styles.resultCard(theme)}
                data-testid={`docs-result-${result.id}`}
              >
                <Box sx={styles.resultCardHeader}>
                  <Typography variant="body2" sx={styles.resultTitle}>
                    {result.title}
                  </Typography>
                  {result.category && (
                    <Chip label={result.category} size="small" sx={styles.resultCategoryChip} />
                  )}
                </Box>
                {(() => {
                  const raw =
                    result.snippet && !result.snippet.includes('No snippet is available')
                      ? result.snippet
                      : result.content?.substring(0, 300)
                  if (!raw) return null
                  // Strip markdown syntax for plain text preview
                  const snippet = raw
                    .replace(/^#{1,6}\s+/gm, '') // headings
                    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
                    .replace(/\*(.+?)\*/g, '$1') // italic
                    .replace(/`([^`]+)`/g, '$1') // inline code
                    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
                    .replace(/^[-*]\s+/gm, '') // list markers
                    .replace(/^\d+\.\s+/gm, '') // ordered list markers
                    .replace(/\n+/g, ' ') // newlines to spaces
                    .trim()
                  return snippet ? (
                    <Typography variant="caption" color="text.secondary" sx={styles.resultSnippet}>
                      {snippet}
                    </Typography>
                  ) : null
                })()}
              </Box>
            ))}
          </Box>
        )}

        {/* No results */}
        {!selectedArticle && hasSearched && results.length === 0 && !isSearching && (
          <Box sx={styles.noResultsContainer}>
            <Typography variant="body2" color="text.secondary" sx={styles.noResultsText}>
              {t('supportAssistant.docs.noResults')}
            </Typography>
            <Chip
              label={t('supportAssistant.docs.askInstead')}
              onClick={handleAskAI}
              color="primary"
              sx={styles.noResultsChip}
            />
          </Box>
        )}

        {/* Empty state — sidebar mode: simple prompt */}
        {!selectedArticle && !hasSearched && !isSearching && !fullWidth && (
          <Box sx={styles.emptyStateContainer}>
            <ArticleOutlined sx={styles.emptyStateIcon(theme)} />
            <Typography variant="body2" color="text.secondary">
              {t('supportAssistant.docs.searchPrompt')}
            </Typography>
          </Box>
        )}

        {/* Browse mode — full-page: category tiles or category article list */}
        {showBrowse && !browsedCategory && (
          <Box>
            <Box sx={styles.categoryGrid}>
              {FEATURED_CATEGORIES.map((cat) => {
                const Icon = cat.icon
                return (
                  <Box
                    key={cat.slug}
                    onClick={() =>
                      handleCategoryClick(
                        cat.slug,
                        t(
                          `helpDocs.categories.${cat.i18nKey}.title`,
                          cat.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                        )
                      )
                    }
                    sx={styles.categoryCard(theme)}
                  >
                    <Box sx={styles.categoryIconWrapper}>
                      <Icon sx={styles.categoryIcon} />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={styles.categoryTitle}>
                        {t(
                          `helpDocs.categories.${cat.i18nKey}.title`,
                          cat.slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                        )}
                      </Typography>
                      <Typography variant="caption" sx={styles.categoryDescription}>
                        {t(`helpDocs.categories.${cat.i18nKey}.description`, '')}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Category article list */}
        {showBrowse && browsedCategory && (
          <Box sx={styles.categoryArticleListContainer}>
            <Box onClick={handleBackToCategories} sx={styles.backToCategoriesButton}>
              <ArrowBackIcon sx={styles.backIcon} />
              <Typography variant="body2" sx={styles.backText}>
                {t('helpDocs.backToCategories', 'Back to categories')}
              </Typography>
            </Box>

            <Typography variant="h6" sx={styles.categoryHeading}>
              {browsedCategory.name}
            </Typography>

            {isLoadingCategory ? (
              <Box sx={styles.categoryLoadingContainer}>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={48}
                    sx={styles.categoryLoadingSkeleton}
                  />
                ))}
              </Box>
            ) : categoryArticles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={styles.categoryEmptyText}>
                {t('helpDocs.noArticlesInCategory', 'No articles in this category')}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary">
                  {t('supportAssistant.docs.categoryArticlesCount', {
                    count: categoryArticles.length,
                  })}
                </Typography>
                {categoryArticles.map((a) => (
                  <Box
                    key={a.slug}
                    onClick={() => handleCategoryArticleClick(a.slug, a.title)}
                    sx={styles.categoryArticleCard(theme)}
                  >
                    <Box sx={styles.categoryArticleCardHeader}>
                      <Typography variant="body2" sx={styles.categoryArticleTitle}>
                        {a.title}
                      </Typography>
                      <Chip
                        label={browsedCategory.name}
                        size="small"
                        sx={styles.categoryArticleChip}
                      />
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
